/**
 * Welke panelen deze gebruiker heeft ingeklapt — per account, op de server.
 *
 * 01-10-2026 (Jordy): elke tabel op Earnings en My Earnings los in- en
 * uitklappen; de stand blijft staan tot je hem zelf weer wijzigt, ook na
 * herladen of opnieuw inloggen, en op elk apparaat. Het is per account: je
 * eigen stand raakt die van een andere gebruiker niet. De server bewaart het
 * in admin_users.ui_prefs (alleen via /auth/me/ui-prefs, alleen je eigen rij).
 *
 * De voorkeuren worden één keer per ingelogd token opgehaald en gedeeld door
 * alle panelen op de pagina. Een wissel is meteen zichtbaar en gaat daarna
 * naar de server; lukt dat niet, dan blijft de stand voor deze sessie staan.
 */
import { useEffect, useState } from 'react'
import { api, getToken } from './api'

const PAD = '/api/admin/auth/me/ui-prefs'

let voorToken = ''
let voorkeuren: Record<string, unknown> = {}
let laden: Promise<void> | null = null
/** Voor welk token de voorkeuren binnen zijn (of het ophalen mislukte). */
let klaarVoor = ''
const luisteraars = new Set<() => void>()

function seintje() {
  for (const f of luisteraars) f()
}

function zorgDatGeladen(): Promise<void> {
  const token = getToken()
  if (token !== voorToken) {
    // Een andere gebruiker (of opnieuw ingelogd): niets van de vorige houden.
    voorToken = token
    voorkeuren = {}
    laden = null
  }
  if (!laden) {
    laden = api.get<{ prefs?: Record<string, unknown> }>(PAD)
      .then((r) => { if (getToken() === token) { voorkeuren = r?.prefs ?? {}; seintje() } })
      .catch(() => { /* geen voorkeuren: alles uitgeklapt */ })
      .finally(() => { if (getToken() === token) { klaarVoor = token; seintje() } })
  }
  return laden
}

/** `[ingeklapt, wissel]` voor één paneel. Zonder sleutel: altijd uitgeklapt. */
export function useInklap(sleutel: string | null | undefined): [boolean, () => void] {
  const [, setTik] = useState(0)
  useEffect(() => {
    if (!sleutel) return
    const f = () => setTik((t) => t + 1)
    luisteraars.add(f)
    // 19-10-2026: ook opnieuw tekenen als het laden al klaar was. Kwam het
    // antwoord tussen het renderen en dit effect binnen, dan miste dit scherm
    // het seintje en bleef het op "Loading..." staan (gezien in Firefox).
    void zorgDatGeladen().then(f)
    return () => { luisteraars.delete(f) }
  }, [sleutel])
  const dicht = Boolean(sleutel && getToken() === voorToken && voorkeuren[sleutel] === true)
  const wissel = () => {
    if (!sleutel) return
    const nieuw = !dicht
    voorkeuren = { ...voorkeuren }
    if (nieuw) voorkeuren[sleutel] = true
    else delete voorkeuren[sleutel]
    seintje()
    // Alleen "ingeklapt" bewaren; uitklappen haalt de voorkeur weg.
    api.patch(PAD, { sleutel, waarde: nieuw ? true : null }).catch(() => { /* zie kop */ })
  }
  return [dicht, wissel]
}

/**
 * Eén willekeurige schermvoorkeur, met dezelfde opslag en cache als useInklap.
 *
 * 02-10-2026 (Jordy): de Chats-pagina krijgt een Easy- en een Pro-stand, per
 * account onthouden. Geeft `[waarde, zet, geladen]`. `geladen` is false zolang
 * de voorkeuren van dit token nog onderweg zijn, zodat een scherm niet eerst
 * de standaard toont (en daar zijn pollers voor start) en dan omslaat. Een
 * waarde gelijk aan de standaard wordt op de server weggehaald, niet bewaard.
 */
export function useUiPref<T extends string | number | boolean>(
  sleutel: string,
  standaard: T,
): [T, (nieuw: T) => void, boolean] {
  const [, setTik] = useState(0)
  useEffect(() => {
    const f = () => setTik((t) => t + 1)
    luisteraars.add(f)
    // 19-10-2026: ook opnieuw tekenen als het laden al klaar was. Kwam het
    // antwoord tussen het renderen en dit effect binnen, dan miste dit scherm
    // het seintje en bleef het op "Loading..." staan (gezien in Firefox).
    void zorgDatGeladen().then(f)
    return () => { luisteraars.delete(f) }
  }, [sleutel])
  const token = getToken()
  const eigen = token === voorToken
  const ruw = eigen ? voorkeuren[sleutel] : undefined
  const waarde = (ruw === undefined || ruw === null || typeof ruw !== typeof standaard)
    ? standaard : (ruw as T)
  const geladen = eigen && klaarVoor === token
  const zet = (nieuw: T) => {
    voorkeuren = { ...voorkeuren }
    if (nieuw === standaard) delete voorkeuren[sleutel]
    else voorkeuren[sleutel] = nieuw
    seintje()
    api.patch(PAD, { sleutel, waarde: nieuw === standaard ? null : nieuw })
      .catch(() => { /* zie kop: de stand blijft voor deze sessie staan */ })
  }
  return [waarde, zet, geladen]
}
